import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../utils/api";
import { useAuth } from "./AuthContext";
import toast from "react-hot-toast";
import { trackAddToCart, trackRemoveFromCart } from "../utils/analytics";
import { pixelAddToCart } from "../utils/metaPixel";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  // Initialize with local cart if present
  const getLocalCart = () => {
    try {
      const stored = localStorage.getItem("akbLocalCart");
      return stored ? JSON.parse(stored) : { items: [] };
    } catch {
      return { items: [] };
    }
  };

  const [cart, setCart] = useState(getLocalCart);
  const [loading, setLoading] = useState(false);
  const [isSideCartOpen, setIsSideCartOpen] = useState(false);

  const toggleCart = (open) => {
    if (open !== undefined) {
      setIsSideCartOpen(open);
    } else {
      setIsSideCartOpen(!isSideCartOpen);
    }
  };

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      setCart(getLocalCart());
      return;
    }
    
    try {
      setLoading(true);
      const res = await api.get("/cart");
      setCart(res.data.cart || { items: [] });
    } catch {
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { 
    fetchCart(); 
  }, [fetchCart]);

  // Expose a public sync function to be called instantly after login/signup
  const syncLocalCartToBackend = async () => {
    const local = getLocalCart();
    if (local.items.length === 0) return;

    try {
      setLoading(true);
      // Sync items one-by-one to preserve backend logic
      for (const item of local.items) {
        await api.post("/cart/add", { productId: item.productId._id, quantity: item.quantity });
      }
      // Clear local cart after successful sync
      localStorage.removeItem("akbLocalCart");
      
      // Fetch merged cart from backend
      const res = await api.get("/cart");
      setCart(res.data.cart || { items: [] });
    } catch (err) {
      console.error("Cart sync failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productDetails, quantity = 1) => {
    // Determine ID from param
    let productId = typeof productDetails === 'string' ? productDetails : productDetails._id;
    
    if (!isAuthenticated) {
      // Local cart handling
      const currentCart = getLocalCart();
      const existingIdx = currentCart.items.findIndex(
        i => (i.productId?._id || i.productId) === productId
      );
      
      let productMeta = typeof productDetails === 'object' ? productDetails : { _id: productId };
      
      // If we don't have full details, try to fetch it for the local UI to display nicely
      if (typeof productDetails === 'string') {
        try {
          const res = await api.get(`/products/${productId}`);
          productMeta = res.data.product;
        } catch(e) {}
      }

      if (existingIdx >= 0) {
        const item = currentCart.items[existingIdx];
        if ((item.quantity + quantity) > (productMeta.maxCartQuantity || 10)) {
           toast.error(`Max ${productMeta.maxCartQuantity || 10} units allowed`);
           return;
        }
        item.quantity += quantity;
      } else {
        currentCart.items.push({ productId: productMeta, quantity });
      }
      
      localStorage.setItem("akbLocalCart", JSON.stringify(currentCart));
      setCart(currentCart);
      trackAddToCart(productMeta, quantity);
      pixelAddToCart(productMeta, quantity);
      toast.success("Added to cart!");
      return;
    }

    // Authenticated handling
    try {
      setLoading(true);
      const res = await api.post("/cart/add", { productId, quantity });
      setCart(res.data.cart);
      const pd = typeof productDetails === "object" ? productDetails : { _id: productId, sellPrice: 0 };
      trackAddToCart(pd, quantity);
      pixelAddToCart(pd, quantity);
      toast.success("Added to cart!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not add to cart");
    } finally { 
      setLoading(false); 
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (!isAuthenticated) {
      const currentCart = getLocalCart();
      if (quantity <= 0) {
        currentCart.items = currentCart.items.filter(i => (i.productId?._id || i.productId) !== productId);
      } else {
        const item = currentCart.items.find(i => (i.productId?._id || i.productId) === productId);
        if (item) {
          const limit = item.productId?.maxCartQuantity || 10;
          if (quantity > limit) {
             toast.error(`Max ${limit} allowed`);
             return;
          }
          item.quantity = quantity;
        }
      }
      localStorage.setItem("akbLocalCart", JSON.stringify(currentCart));
      setCart(currentCart);
      return;
    }

    try {
      const res = await api.put("/cart/update", { productId, quantity });
      setCart(res.data.cart);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not update cart");
    }
  };

  const removeFromCart = async (productId) => {
    if (!isAuthenticated) {
      const currentCart = getLocalCart();
      const removedItem = currentCart.items.find(i => (i.productId?._id || i.productId) === productId);
      currentCart.items = currentCart.items.filter(i => (i.productId?._id || i.productId) !== productId);
      localStorage.setItem("akbLocalCart", JSON.stringify(currentCart));
      setCart(currentCart);
      if (removedItem) trackRemoveFromCart(removedItem.productId, removedItem.quantity);
      toast.success("Removed from cart");
      return;
    }

    try {
      const removedItem = cart.items.find(i => (i.productId?._id || i.productId) === productId);
      const res = await api.delete(`/cart/remove/${productId}`);
      setCart(res.data.cart);
      if (removedItem) trackRemoveFromCart(removedItem.productId, removedItem.quantity);
      toast.success("Removed from cart");
    } catch {}
  };

  const clearCart = async () => {
    if (!isAuthenticated) {
      localStorage.removeItem("akbLocalCart");
      setCart({ items: [] });
      return;
    }
    
    try {
      await api.delete("/cart/clear");
      setCart({ items: [] });
    } catch {}
  };

  const cartTotal = cart.items.reduce((sum, item) => {
    // Handling case where productId is directly an ID instead of populated object locally
    const price = item.productId?.sellPrice || 0;
    return sum + price * item.quantity;
  }, 0);

  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart, cartTotal, cartCount, syncLocalCartToBackend, isSideCartOpen, setIsSideCartOpen, toggleCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
